import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Lock, ExternalLink, Menu, X } from 'lucide-react';
import { API, inputCls } from '@/lib/api';
import { TOKEN_KEY } from '@/context/LanguageContext';
import { SECTIONS } from '@/admin/schema';
import { AdminContentProvider, apiError } from '@/admin/AdminContentContext';
import ContentEditor from '@/admin/ContentEditor';
import Submissions from '@/admin/pages/Submissions';
import MediaLibrary from '@/admin/pages/MediaLibrary';
import UsersPage from '@/admin/pages/UsersPage';
import ChatLogs from '@/admin/pages/ChatLogs';

function LoginView({ onLogin }) {
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      if (!['admin', 'editor'].includes(data.user?.role)) { toast.error('هذا الحساب لا يملك صلاحية الدخول إلى اللوحة'); return; }
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
    } catch (err) { toast.error(apiError(err, 'البريد الإلكتروني أو كلمة المرور غير صحيحة')); } finally { setLoading(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-brand-paper px-5" dir="rtl" data-testid="admin-login-view">
      <form onSubmit={submit} className="w-full max-w-md rounded-[2rem] border border-brand-line bg-white p-8 shadow-[0_40px_80px_-40px_rgba(15,23,42,0.25)]" data-testid="admin-login-form">
        <span className="grid h-12 w-12 place-items-center rounded-2xl border border-brand-blue/15 bg-brand-blue/8 text-brand-blue"><Lock className="h-5 w-5" /></span>
        <h1 className="mt-5 font-display text-2xl font-extrabold text-brand-ink">لوحة إدارة WiyakGPS</h1>
        <p className="mt-1 text-sm text-brand-steel">الدخول مقيّد بفريق WiyakGPS (مدير النظام / محرر المحتوى).</p>
        <div className="mt-6 space-y-4">
          <label className="block"><span className="mb-1.5 block text-xs font-bold text-brand-steel">البريد الإلكتروني</span>
            <input data-testid="admin-email-input" type="email" dir="ltr" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className={inputCls} /></label>
          <label className="block"><span className="mb-1.5 block text-xs font-bold text-brand-steel">كلمة المرور</span>
            <input data-testid="admin-password-input" type="password" dir="ltr" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={inputCls} /></label>
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-6 w-full rounded-full bg-brand-blue py-3.5 text-sm font-bold text-white transition-all hover:bg-brand-blueHover disabled:opacity-60">{loading ? 'جارٍ الدخول…' : 'تسجيل الدخول'}</button>
        <Link to="/" className="mt-4 block text-center text-xs font-bold text-brand-steel hover:text-brand-blue">العودة إلى الموقع</Link>
      </form>
    </div>
  );
}

function Sidebar({ user, active, onLogout, open, setOpen }) {
  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-brand-ink/40 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 start-0 z-50 flex w-64 flex-col bg-brand-ink text-white transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${open ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`} data-testid="admin-sidebar">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
          <img src="/logo.jpeg" alt="WiyakGPS" className="h-9 w-9 rounded-full ring-2 ring-white/20" />
          <div><p className="font-display text-sm font-extrabold">WiyakGPS</p><p className="font-mono text-[10px] uppercase tracking-widest text-white/50">لوحة الإدارة</p></div>
          <button type="button" className="ms-auto grid h-8 w-8 place-items-center rounded-lg text-white/60 lg:hidden" onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4" data-lenis-prevent>
          {SECTIONS.filter((s) => !s.adminOnly || user.role === 'admin').map((s) => (
            <Link key={s.id} to={`/admin/${s.id}`} onClick={() => setOpen(false)} data-testid={`admin-nav-${s.id}`}
              className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-bold transition-colors ${active === s.id ? 'bg-brand-blue text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </Link>
          ))}
        </nav>
        <div className="border-t border-white/10 px-5 py-4">
          <p className="truncate text-xs font-bold" data-testid="admin-user-name">{user.name || user.email}</p>
          <p className="font-mono text-[10px] text-white/50" dir="ltr">{user.email} · <span data-testid="admin-user-role">{user.role === 'admin' ? 'مدير النظام' : 'محرر محتوى'}</span></p>
          <div className="mt-3 flex gap-2">
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-open-site" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-[11px] font-bold text-white/80 hover:bg-white/5"><ExternalLink className="h-3.5 w-3.5" /> الموقع</a>
            <button type="button" onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-[11px] font-bold text-white/80 hover:bg-brand-crimson/30"><LogOut className="h-3.5 w-3.5" /> خروج</button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default function Admin() {
  const { section = 'home' } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [user, setUser] = useState(undefined); // undefined = checking
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(null); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(['admin', 'editor'].includes(data.role) ? data : null))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); });
  }, []);

  useEffect(() => { if (pathname === '/admin' || pathname === '/admin/') navigate('/admin/home', { replace: true }); }, [pathname, navigate]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem('wiyak-preview'); setUser(null); };

  if (user === undefined) return <div className="grid min-h-screen place-items-center bg-brand-paper text-sm text-brand-dim" data-testid="admin-checking">جارٍ التحقق…</div>;
  if (!user) return <LoginView onLogin={setUser} />;

  const current = SECTIONS.find((s) => s.id === section) || SECTIONS[0];
  if (current.adminOnly && user.role !== 'admin') navigate('/admin/home', { replace: true });

  return (
    <AdminContentProvider user={user}>
      <div className="flex min-h-screen bg-brand-paper" dir="rtl" data-testid="admin-panel-view">
        <Sidebar user={user} active={current.id} onLogout={logout} open={open} setOpen={setOpen} />
        <main className="min-w-0 flex-1 px-4 py-5 lg:px-8">
          <div className="mb-4 flex items-center gap-3 lg:hidden">
            <button type="button" onClick={() => setOpen(true)} data-testid="admin-menu-btn" className="grid h-10 w-10 place-items-center rounded-xl border border-brand-line bg-white text-brand-ink"><Menu className="h-5 w-5" /></button>
            <span className="font-display text-base font-extrabold text-brand-ink">{current.label}</span>
          </div>
          {current.id === 'submissions' && <Submissions />}
          {current.id === 'media' && <MediaLibrary />}
          {current.id === 'users' && <UsersPage user={user} />}
          {current.groups && <ContentEditor section={current} />}
          {current.id === 'assistant' && <ChatLogs />}
        </main>
      </div>
    </AdminContentProvider>
  );
}
